import { useEffect, useState } from 'react'
import { MapPin, Plus } from 'lucide-react'
import OptionCard from '@/components/storefront/OptionCard'
import Skeleton from '@/components/ui/Skeleton'
import { useToast } from '@/components/ui/toast'
import { errorMessage } from '@/lib/api'
import { useAddresses, useDeleteAddress, useSetDefaultAddress } from '@/lib/addresses'
import type { CustomerAddress } from '@/types'
import AddressModal from './AddressModal'

/**
 * The saved-address book. At checkout it is a picker: pass value/onChange and
 * it auto-selects the default (or only) address so returning customers sail
 * through. Without onChange (the account page) it is a plain manager - cards
 * are not selectable but edit, delete and set-default still work.
 */
export default function AddressBook({
  value = null,
  onChange,
}: {
  value?: number | null
  onChange?: (id: number | null) => void
}) {
  const toast = useToast()
  const addresses = useAddresses()
  const remove = useDeleteAddress()
  const setDefault = useSetDefaultAddress()

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CustomerAddress | null>(null)

  const list = addresses.data ?? []

  // Keep the selection valid: pick the default (list is default-first) when
  // nothing is selected, and fall back if the selected address was deleted.
  useEffect(() => {
    if (!onChange || !addresses.data) return
    const stillThere = value != null && addresses.data.some((a) => a.id === value)
    if (!stillThere) onChange(addresses.data[0]?.id ?? null)
  }, [addresses.data, value, onChange])

  const openAdd = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (address: CustomerAddress) => {
    setEditing(address)
    setModalOpen(true)
  }

  const deleteAddress = (address: CustomerAddress) => {
    remove.mutate(address.id, {
      onSuccess: () => toast.success('Address removed'),
      onError: (error) => toast.error('Could not remove address', errorMessage(error)),
    })
  }

  return (
    <div className="space-y-2">
      {addresses.isPending && (
        <>
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </>
      )}

      {list.map((address) => (
        <div key={address.id}>
          <OptionCard
            selected={value === address.id}
            onSelect={() => onChange?.(address.id)}
            icon={<MapPin className="size-4 text-primary" />}
            title={address.recipient_name}
            badge={address.is_default ? 'Default' : undefined}
            description={
              <>
                {address.phone}
                <span className="block">
                  {address.line1}
                  {address.line2 ? `, ${address.line2}` : ''}, {address.postcode} {address.city}, {address.state}
                </span>
              </>
            }
          />
          <div className="mt-1 flex gap-4 px-3 text-xs font-semibold text-foreground/60">
            <button type="button" className="hover:text-foreground" onClick={() => openEdit(address)}>
              Edit
            </button>
            {!address.is_default && (
              <button type="button" className="hover:text-foreground" onClick={() => setDefault.mutate(address.id)}>
                Set as default
              </button>
            )}
            <button type="button" className="hover:text-danger" onClick={() => deleteAddress(address)}>
              Delete
            </button>
          </div>
        </div>
      ))}

      {!addresses.isPending && (
        <button
          type="button"
          onClick={openAdd}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-foreground/20 p-3 text-sm font-semibold text-foreground/60 transition-colors hover:border-foreground/40 hover:text-foreground"
        >
          <Plus className="size-4" />
          {list.length === 0 ? 'Add a delivery address' : 'Add new address'}
        </button>
      )}

      <AddressModal
        open={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
        onSaved={(address) => onChange?.(address.id)}
      />
    </div>
  )
}
